import { runEnvironmentTaskRequest } from "Api/organizations/projects/environments/tasks/runEnvironmentTask.request";
import { createApiErrorPayload } from "src/common/utils/apiErrorHandling";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";

import { selectEnvironmentTaskByName } from "../environmentTasks.selectors";
import { getEnvironmentTasksKey } from "../utils";

import { fetchEnvironmentTasksThunk } from "./fetchEnvironmentTasks.thunk";

import type { EnvironmentTasksParams } from "../types";

export interface RunEnvironmentTaskParams extends EnvironmentTasksParams {
  taskName: string;
  variables?: Record<string, unknown>;
}

/**
 * Fires a task run, then refreshes the data layer so the row flips to Running.
 * Posts the task's `#run` href with the optional per-task `variables` body. The
 * card guards this behind the run modal (prod-run footgun) and returns the new
 * activity id so the modal can deep-link to its log.
 */
export const runEnvironmentTaskThunk = createAppAsyncThunk(
  "environmentTasks/run",
  async (
    params: RunEnvironmentTaskParams,
    { dispatch, getState, rejectWithValue }
  ) => {
    const { taskName } = params;
    const task = selectEnvironmentTaskByName(getState(), params);
    const runHref = task?._links?.["#run"]?.href;

    if (!runHref) {
      return rejectWithValue({
        error: `No run link available for task "${taskName}".`
      });
    }

    try {
      const { _embedded } = await runEnvironmentTaskRequest({
        runHref,
        variables: params.variables
      });
      // Refresh definitions + activities so status/last-run reflect the new run.
      // The run has already succeeded here, so a transient refetch failure must
      // not reject the thunk: that would show the modal a "run failed" error for
      // a task that did start, inviting a duplicate run of a prod task. Swallow
      // it instead — a stale row self-corrects on the next refetch/poll.
      try {
        await dispatch(fetchEnvironmentTasksThunk(params)).unwrap();
      } catch {
        // run already succeeded; ignore the refresh failure
      }
      return {
        key: getEnvironmentTasksKey(params),
        taskName,
        activityId: _embedded?.activities?.[0]?.id
      };
    } catch (error) {
      return rejectWithValue(createApiErrorPayload(error));
    }
  }
);
